import { format } from 'date-fns';

import { BlockerItem } from '../pages/YourTeams/components/SprintSummary/types';

import { supabase } from './supabase';

interface RawTicketCheckIn {
  id: string;
  blockers: string;
  created_at: string;
  description: string;
  check_in: {
    user: {
      id: string;
      first_name: string;
      last_name: string;
    };
  };
  ticket: {
    id: string;
    ticket_number: string;
    title: string;
    external_link: string;
  };
}

export async function fetchBlockersForSprint(
  sprintId?: string,
): Promise<BlockerItem[]> {
  if (!sprintId) return [];

  // First get all tickets for the sprint
  const { data: tickets, error: ticketsError } = await supabase
    .from('tickets')
    .select('id')
    .eq('sprint_id', sprintId)
    .is('removed_at', null);

  if (ticketsError) {
    throw new Error(`Failed to fetch tickets: ${ticketsError.message}`);
  }
  if (!tickets) return [];

  const ticketIds = tickets.map((ticket: { id: string }) => ticket.id);

  // Get the latest ticket check-in for each ticket
  const { data: checkIns, error: checkInsError } = await supabase
    .from('ticket_check_ins')
    .select(
      `
      id,
      blockers,
      created_at,
      description,
      check_in:check_in_id (
        user:user_id (
          id,
          first_name,
          last_name,
          role,
          type
        )
      ),
      ticket:tickets (
        id,
        ticket_number,
        title,
        external_link
      )
    `,
    )
    .in('ticket_id', ticketIds)
    .eq('check_in_type', 'ticket')
    .eq('check_in.user.type', 'internal')
    .not('check_in.user.role', 'eq', 'shadow_engineer')
    .not('check_in', 'is', null)
    .not('check_in.user', 'is', null)
    .order('created_at', { ascending: false });

  if (checkInsError) {
    throw new Error(`Failed to fetch check-ins: ${checkInsError.message}`);
  }
  if (!checkIns || checkIns.length === 0) return [];

  // Get only the latest check-in for each ticket and filter for blockers
  const processedTicketIds = new Set<string>();
  const latestCheckIns = (checkIns as unknown as RawTicketCheckIn[]).reduce(
    (acc, checkIn) => {
      // Skip if we've already processed this ticket
      if (processedTicketIds.has(checkIn.ticket.id)) {
        return acc;
      }

      // Mark this ticket as processed
      processedTicketIds.add(checkIn.ticket.id);

      // Only add to accumulator if it has blockers
      if (checkIn.blockers && checkIn.blockers.trim()) {
        acc[checkIn.ticket.id] = checkIn;
      }

      return acc;
    },
    {} as Record<string, RawTicketCheckIn>,
  );

  // Format the data for the Blockers component
  return Object.values(latestCheckIns).map((checkIn) => ({
    id: checkIn.id,
    title: `${checkIn.ticket.ticket_number}: ${checkIn.ticket.title}`,
    description: checkIn.blockers,
    ticketDescription: checkIn.description,
    externalLink: checkIn.ticket.external_link,
    dateAdded: format(new Date(checkIn.created_at), 'MMM d, yyyy'),
    user: {
      id: checkIn.check_in.user.id,
      firstName: checkIn.check_in.user.first_name,
      lastName: checkIn.check_in.user.last_name,
    },
    checkIn: {
      id: checkIn.id,
      createdAt: format(new Date(checkIn.created_at), 'MMM d, yyyy'),
      ticket: {
        id: checkIn.ticket.id,
        ticketNumber: checkIn.ticket.ticket_number,
        title: checkIn.ticket.title,
      },
    },
  }));
}
